import React from "react";
import { Form, Input, Switch, Typography } from "antd";
import { CONTROL } from "pages/MarketingPages/FieldInput";

const { Text } = Typography;

/**
 * The fields of one redirect rule.
 *
 * Split from the screen so the list logic and the form's validation stay
 * legible separately — the same shape the FAQ screen uses. The parent owns the
 * antd `Form` instance and does the submitting; this only declares the fields.
 *
 * Validation here is deliberately thin. The API normalises both paths (leading
 * and trailing slash) and is the only thing that can rule on uniqueness, so a
 * second opinion in the browser would only be a chance to disagree with it.
 * What is checked is the one mistake a round trip cannot make obvious: a
 * destination identical to the source.
 */
export default function RedirectForm({ form, onSubmit }) {
  return (
    <Form form={form} layout="vertical" onFinish={onSubmit} requiredMark={false}>
      <Form.Item
        name="from"
        label="Стар адрес"
        extra={
          <Text type="secondary">
            Пътят, на който все още идват посетители, напр. <code>/kotki/hranene/</code>
          </Text>
        }
        rules={[
          { required: true, whitespace: true, message: "Старият адрес е задължителен" },
          {
            // The server enforces this too; catching it here saves a round trip
            // on the one input people habitually paste a full URL into.
            validator: (_, value) =>
              !value || value.trim().startsWith("/") || /^https?:\/\//i.test(value.trim())
                ? Promise.resolve()
                : Promise.reject(new Error("Старият адрес трябва да започва с /")),
          },
        ]}
      >
        <Input style={CONTROL} placeholder="/staro-mesto/" />
      </Form.Item>

      <Form.Item
        name="to"
        label="Нов адрес"
        extra={
          <Text type="secondary">Път в сайта или пълен адрес към друг сайт</Text>
        }
        rules={[
          { required: true, whitespace: true, message: "Новият адрес е задължителен" },
          {
            validator: (_, value) => {
              const from = form.getFieldValue("from");
              // Compared loosely, because the server will normalise both sides:
              // `/x` and `/x/` become the same rule and would be rejected there.
              const same =
                value &&
                from &&
                value.trim().replace(/\/+$/, "") === from.trim().replace(/\/+$/, "");
              return same
                ? Promise.reject(new Error("Пренасочването не може да сочи към себе си"))
                : Promise.resolve();
            },
          },
        ]}
      >
        <Input style={CONTROL} placeholder="/novo-mesto/" />
      </Form.Item>

      <Form.Item
        name="permanent"
        label="Постоянно (301)"
        valuePropName="checked"
        extra={
          <Text type="secondary">
            Остави включено, освен ако старият адрес няма да се върне — постоянното
            пренасочване пренася класирането в търсачките към новата страница.
          </Text>
        }
      >
        <Switch />
      </Form.Item>

      <Form.Item name="note" label="Бележка (незадължително)">
        <Input.TextArea rows={2} maxLength={500} placeholder="Защо съществува това правило" />
      </Form.Item>
    </Form>
  );
}
